import Box from "@mui/material/Box";
import { keyframes } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useRef } from "react";

import {
  BOOT_INDICATOR_FADE_IN,
  BOOT_INDICATOR_FADE_OUT,
  BOOT_INDICATOR_INTENSITY,
  BOOT_INDICATOR_LABELS,
  type BootIndicatorLook,
} from "@/ui/bootIndicatorVisuals";

const sweep = keyframes`
  to { transform: translateY(400%); }
`;

/**
 * A light band that sweeps down the rail's right edge. Stays mounted while hidden so it can fade out.
 *
 * @param look - The look to show, or `undefined` to fade out.
 */
export default function BootShimmer({ look }: { look: BootIndicatorLook | undefined }) {
  // Keep the last active look while fading out, so the band keeps its colour and position.
  const lastLook = useRef(look);
  useEffect(() => {
    if (look) lastLook.current = look;
  }, [look]);
  const shownLook = look ?? lastLook.current;
  const intensity = BOOT_INDICATOR_INTENSITY[shownLook ?? "background"];
  const label = look ? BOOT_INDICATOR_LABELS[look] : "";

  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          right: "-1px",
          width: "2px",
          height: "100%",
          overflow: "hidden",
          pointerEvents: "none",
          opacity: look ? intensity.opacity : 0,
          transition: look ? BOOT_INDICATOR_FADE_IN : BOOT_INDICATOR_FADE_OUT,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: "-34%",
            width: "100%",
            height: "34%",
            background: `linear-gradient(to bottom, transparent, ${intensity.color} 50%, transparent)`,
            boxShadow: `0 0 ${intensity.glowPx}px ${intensity.color}`,
            animation: shownLook
              ? `${sweep} ${intensity.durationSeconds}s cubic-bezier(.45,.05,.55,.95) infinite ${intensity.reverse ? "reverse" : "normal"}`
              : "none",
            // Pausing rather than removing the animation holds the band in place through the fade-out.
            animationPlayState: look ? "running" : "paused",
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              top: 0,
              height: "100%",
              opacity: 0.7,
              background: `linear-gradient(to bottom, transparent, ${intensity.color} 30%, ${intensity.color} 70%, transparent)`,
            },
          }}
        />
      </Box>
      {look && (
        <Tooltip title={label} placement="right" arrow>
          <Box
            role="status"
            aria-label={label}
            sx={{ position: "absolute", top: 0, right: "-4px", width: "9px", height: "100%", zIndex: 1 }}
          />
        </Tooltip>
      )}
    </>
  );
}
